import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import {
  AlertCircle,
  Clock,
  HelpCircle,
  List,
  MessageSquare,
  Monitor,
  MousePointer2,
  ShoppingCart,
  Smartphone,
  Tablet,
  X,
} from 'lucide-react';
import { closeParticipantSidebar } from '../store/analyticsSlice';
import { selectSelectedParticipant } from '../store/analyticSelector';
import { RadialScoreChart } from './RadialScoreChart';
import { cn } from '@/lib/utils';
import CurvedCorner from '@/components/ui/CurvedCorner';
import type { ParticipantMessage, ParticipantQuiz, ParticipantSurvey } from '../types';
import { useTranslation } from 'react-i18next';
export const ParticipantDetailSidebar = () => {
  const dispatch = useDispatch();
  const { isOpen, details, loading } = useSelector(selectSelectedParticipant);
  const { t } = useTranslation('webinars');
  const { t: tA } = useTranslation('analytics');
  const [activeTab, setActiveTab] = useState<'messages' | 'surveys' | 'quizzes'>('messages');

  if (!isOpen) {
    return null;
  }

  const interactions = details?.interactions;
  const totalDone =
    (details?.engagement.participatedQuizzesCount || 0) +
    (details?.engagement.participatedSurveysCount || 0);
  const totalAvailable =
    (details?.engagement.totalQuizzesInSession || 0) +
    (details?.engagement.totalSurveysInSession || 0);

  const offerInteractionStatus = details?.engagement.offerInteractionStatus;
  let offerCardBgClass: string;
  let offerIconClass: string;
  let offerLabelClass: string;
  let offerStatusLabel: string;
  if (offerInteractionStatus === 'CONVERTED') {
    offerCardBgClass = 'bg-[#C0FFEA]';
    offerIconClass = 'text-[#17805C]';
    offerLabelClass = 'text-[#17805C]';
    offerStatusLabel = t('participantDetail.offerInteractionStatus.converted');
  } else if (offerInteractionStatus === 'CLICKED') {
    offerCardBgClass = 'bg-[#EAF0FF]';
    offerIconClass = 'text-[#1A4199]';
    offerLabelClass = 'text-[#1A4199]';
    offerStatusLabel = t('participantDetail.offerInteractionStatus.clicked');
  } else {
    offerCardBgClass = 'bg-[#F3F3F4]';
    offerIconClass = 'text-role-secondary';
    offerLabelClass = 'text-role-secondary';
    offerStatusLabel = t('participantDetail.offerInteractionStatus.noClick');
  }

  const getDeviceIcon = () => {
    const device = details?.technical.device?.toLowerCase();
    if (device === 'mobile') {
      return <Smartphone className="text-primary w-10 h-10" />;
    }
    if (device === 'tablet') {
      return <Tablet className="text-primary w-10 h-10" />;
    }
    return <Monitor className="text-primary w-10 h-10" />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) {
      return '--/--/----';
    }
    const dateTimeInstance = DateTime.fromISO(dateString);
    if (dateTimeInstance.isValid) {
      return dateTimeInstance.setLocale('fr').toFormat('dd/MM/yyyy');
    }
    return '--/--/----';
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end font-poppins">
      <div
        className="absolute inset-0 bg-dark/20 backdrop-blur-sm transition-opacity"
        onClick={() => dispatch(closeParticipantSidebar())}
      />

      <div className="relative w-full max-w-[784px] h-full bg-white shadow-2xl flex flex-col animate-slide-in-right">
        <div className="h-[142px] bg-[#f3f3f4] p-6 flex justify-between items-start shrink-0">
          <div className="flex gap-4 items-center">
            <div className="w-20 h-20 rounded-2xl bg-white shadow-sm flex items-center justify-center overflow-hidden">
              {getDeviceIcon()}
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-2xl font-semibold text-[#0c0e21]">
                {details?.profile.firstName} {details?.profile.lastName}
              </h2>
              <p className="text-sm font-medium text-[#0c0e21] opacity-70">
                {details?.profile.email} /{' '}
                {details?.profile.phoneNumber || tA('participantDetail.noPhone')}
              </p>
              <p className="text-[12px] text-[#6d6e7a]">
                {details?.registeredAt
                  ? tA('participantDetail.registered', {
                      date: formatDate(details.registeredAt),
                      country: details.profile.countryCode || 'France',
                    })
                  : tA('participantDetail.loading')}
              </p>
            </div>
          </div>
          <button
            onClick={() => dispatch(closeParticipantSidebar())}
            className="p-2 hover:bg-white rounded-full transition-colors cursor-pointer"
          >
            <X className="w-6 h-6 text-[#0c0e21]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-8 flex flex-col gap-8 custom-scrollbar">
          <div className="bg-[#fafafa] border border-[#f3f3f4] rounded-[16px] p-6 flex items-center justify-between">
            <div className="flex flex-col items-center gap-4 mt-2">
              <span className="text-sm font-medium text-[#2e2e30]">
                {tA('participantDetail.scoreLabel')}
              </span>
              <RadialScoreChart
                score={details?.engagement.globalScore || 0}
                percentScore={details?.engagement.relativeScorePercentage || 0}
              />
            </div>

            <div className="flex flex-col gap-4 w-[270px]">
              <StatRow
                label={tA('participantDetail.presence')}
                value={details?.engagement.watchTimeDuration || '00:00:00'}
                Icon={Clock}
              />
              <StatRow
                label={tA('participantDetail.interactions')}
                value={`${totalDone}`}
                unit={`/ ${totalAvailable}`}
                Icon={MousePointer2}
              />
            </div>

            <div className="bg-white border border-[#f3f3f4] rounded-lg p-4 flex flex-col items-center justify-center gap-2 w-[186px] h-[160px]">
              <div className={cn('p-3 rounded-xl', offerCardBgClass)}>
                <ShoppingCart className={offerIconClass} />
              </div>
              <span className="text-[12px] text-[#6d6e7a]">
                {t('participantDetail.offerLabel')}
              </span>
              <span
                className={cn('text-[20px] font-medium leading-tight text-center', offerLabelClass)}
              >
                {offerStatusLabel}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <div className="flex border-b border-[#f3f3f4] overflow-x-auto no-scrollbar">
              <TabItem
                label={tA('participantDetail.tabs.messages', {
                  count: interactions?.messages.length || 0,
                })}
                active={activeTab === 'messages'}
                onClick={() => setActiveTab('messages')}
                Icon={MessageSquare}
              />
              <TabItem
                label={tA('participantDetail.tabs.surveys', {
                  done: interactions?.surveys.length || 0,
                  total: details?.engagement.totalSurveysInSession || 0,
                })}
                active={activeTab === 'surveys'}
                onClick={() => setActiveTab('surveys')}
                Icon={List}
              />
              <TabItem
                label={tA('participantDetail.tabs.quizzes', {
                  done: interactions?.quizzes.length || 0,
                  total: details?.engagement.totalQuizzesInSession || 0,
                })}
                active={activeTab === 'quizzes'}
                onClick={() => setActiveTab('quizzes')}
                Icon={HelpCircle}
              />
            </div>

            <div className="min-h-[300px] pb-10">
              {loading ? (
                <div className="p-20 flex flex-col items-center gap-4">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                  <p className="text-role-tertiary font-medium">
                    {tA('participantDetail.loadingActivity')}
                  </p>
                </div>
              ) : (
                <InteractionList type={activeTab} data={interactions?.[activeTab] || []} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractionList = ({ type, data }: { type: string; data: any[] }) => {
  const { t } = useTranslation('analytics');
  if (!data || data.length === 0) {
    return (
      <div className="py-20 flex flex-col items-center justify-center border-2 border-dashed border-[#f3f3f4] rounded-2xl bg-[#fafafa]">
        <AlertCircle className="w-10 h-10 text-[#cecfd3] mb-4" />
        <p className="text-role-tertiary font-medium italic">{t('participantDetail.noElement')}</p>
      </div>
    );
  }

  const formatTime = (dateString?: string) => {
    if (!dateString) {
      return '--:--';
    }
    const dateTimeInstance = DateTime.fromISO(dateString);
    if (dateTimeInstance.isValid) {
      return dateTimeInstance.toFormat('HH:mm');
    }
    return '--:--';
  };

  const formatRelativeTime = (seconds?: number) => {
    if (seconds === undefined || seconds === null) return '00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;

    const parts = [m.toString().padStart(2, '0'), s.toString().padStart(2, '0')];

    if (h > 0) parts.unshift(h.toString().padStart(2, '0'));

    return `+${parts.join(':')}`;
  };

  switch (type) {
    case 'messages':
      return (
        <div className="flex flex-col gap-3">
          {data.map((message: ParticipantMessage) => (
            <div
              key={message.id}
              className="flex items-center justify-between border border-[#f3f3f4] h-[52px] px-4 rounded-xl bg-white hover:border-primary/30 transition-all shadow-sm"
            >
              <div className="flex flex-col overflow-hidden">
                <span className="text-[10px] font-bold text-primary uppercase tracking-widest">
                  {message.authorDisplayName}
                </span>
                <p className="text-sm text-[#0c0e21] truncate font-medium">{message.content}</p>
              </div>
              <span className="text-xs text-primary ml-4 shrink-0 font-bold bg-primary/5 px-2 py-1 rounded-md">
                {formatRelativeTime(message.relativeTimeSeconds)}
              </span>
            </div>
          ))}
        </div>
      );

    case 'surveys':
      return (
        <div className="border border-[#f3f3f4] rounded-[20px] overflow-hidden bg-white shadow-sm">
          <div className="flex items-center px-4 h-[48px] bg-[#fafafa] border-b border-[#f3f3f4]">
            <span className="text-[10px] font-bold text-[#6d6e7a] uppercase tracking-widest flex-1">
              {t('participantDetail.surveys.columnText')}
            </span>
            <span className="text-[10px] font-bold text-[#6d6e7a] uppercase tracking-widest w-[120px] text-right">
              {t('participantDetail.surveys.columnTime')}
            </span>
          </div>

          {data.flatMap((survey: ParticipantSurvey) =>
            survey.answers.map((answer, index) => (
              <div
                key={`${survey.id}-${index}`}
                className="flex items-center justify-between border-b border-[#f3f3f4] h-[52px] px-4 last:border-0 hover:bg-[#fafafa] transition-colors"
              >
                <span className="text-sm text-[#0c0e21] flex-1 truncate font-medium">
                  {answer.questionText} :{' '}
                  <span className="text-[#6d6e7a] font-normal">
                    {answer.answerLabel ||
                      answer.freeText ||
                      t('participantDetail.surveys.noAnswer')}
                  </span>
                </span>
                <span className="text-sm text-[#6d6e7a] w-[120px] text-right font-bold">
                  {formatTime(survey.submittedAt)}
                </span>
              </div>
            )),
          )}
        </div>
      );

    case 'quizzes':
      return (
        <div className="flex flex-col gap-4">
          {data.map((quiz: ParticipantQuiz) => (
            <div
              key={quiz.id}
              className="p-5 bg-white border border-[#f3f3f4] rounded-2xl flex justify-between items-center shadow-sm"
            >
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-dark">
                  {quiz.quizTitle || t('participantDetail.quiz.defaultTitle')}
                </span>
                <span className="text-[10px] text-role-tertiary">
                  {t('participantDetail.quiz.completedOn', {
                    date: DateTime.fromISO(quiz.submittedAt).setLocale('fr').toFormat('dd/MM/yyyy'),
                  })}
                </span>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-lg font-black text-primary">
                  {quiz.score}/{quiz.totalPoints}
                </span>
                <span className="text-[9px] uppercase font-bold text-role-tertiary tracking-tighter text-right leading-none">
                  {t('participantDetail.quiz.finalScore')}
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
};

const StatRow = ({
  label,
  value,
  unit,
  Icon,
}: {
  label: string;
  value: string;
  unit?: string;
  Icon: any;
}) => (
  <div className="bg-white border border-[#f3f3f4] rounded-[8px] p-4 flex justify-between items-center shadow-sm hover:shadow-md transition-shadow">
    <div className="flex flex-col">
      <span className="text-[11px] font-bold text-[#6d6e7a] uppercase tracking-widest">
        {label}
      </span>
      <div className="flex items-baseline gap-1">
        <span className="text-[20px] font-bold text-[#0c0e21]">{value}</span>
        {unit && <span className="text-[11px] font-bold text-[#6d6e7a]">{unit}</span>}
      </div>
    </div>
    <div className="p-2.5 bg-[#ebf1ff] rounded-xl shrink-0">
      <Icon className="text-primary w-5 h-5" />
    </div>
  </div>
);

const TabItem = ({
  label,
  active,
  onClick,
  Icon,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  Icon: any;
}) => (
  <div className="relative">
    <button
      onClick={onClick}
      className={cn(
        'px-6 py-4 text-[13px] font-bold transition-all border-b-[3px] flex items-center gap-2 whitespace-nowrap cursor-pointer relative z-20',
        active
          ? 'border-primary text-dark bg-white rounded-t-[14px]'
          : 'border-transparent text-[#6d6e7a] hover:text-dark',
      )}
    >
      <Icon size={16} className={active ? 'text-primary' : 'text-[#6d6e7a]'} />
      {label}
    </button>
    {active && (
      <>
        <div className="absolute -bottom-[1px] -left-5 size-5 z-10">
          <CurvedCorner corner="bottom-right" shadowColor="#ffffff" />
        </div>
        <div className="absolute -bottom-[1px] -right-5 size-5 z-10">
          <CurvedCorner corner="bottom-left" shadowColor="#ffffff" />
        </div>
      </>
    )}
  </div>
);
